import { useEffect } from "react";
import {
  checkoutPagoScriptVersion,
  checkoutPagoScriptIntegrity,
  checkoutScriptVersion,
  checkoutScriptIntegrity,
} from "./checkout";

const loadedScripts = {};
const loadingScripts = {};

function buildScriptAttributes(url, attributes = {}) {
  const merged = { ...attributes };
  let integrity;

  if (url === checkoutPagoScriptVersion) {
    integrity = checkoutPagoScriptIntegrity;
  } else if (url === checkoutScriptVersion) {
    integrity = checkoutScriptIntegrity;
  }

  if (integrity) {
    merged.integrity = integrity;
    merged.crossorigin = "anonymous";
  }

  return merged;
}

/**
 * Loads an external script by URL with deduplication.
 * Returns a cleanup function that removes the script element (optional).
 */
export function loadScript(url, { onLoad, onError, attributes, id } = {}) {
  if (!url) {
    return () => {};
  }

  const scriptAttributes = buildScriptAttributes(url, attributes);

  if (loadedScripts[url]) {
    if (onLoad) {
      onLoad();
    }
    return () => {};
  }

  if (loadingScripts[url]) {
    if (onLoad) {
      loadingScripts[url].push(onLoad);
    }
    if (onError) {
      loadingScripts[url + ":error"] =
        (loadingScripts[url + ":error"] || []).concat(onError);
    }
    return () => {};
  }

  loadingScripts[url] = onLoad ? [onLoad] : [];

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = url;
  script.async = true;

  if (id) {
    script.id = id;
  }

  Object.keys(scriptAttributes).forEach((prop) => {
    script.setAttribute(prop, scriptAttributes[prop]);
  });

  script.onload = () => {
    loadedScripts[url] = true;
    const callbacks = loadingScripts[url] || [];
    delete loadingScripts[url];
    callbacks.forEach((cb) => cb());
  };

  script.onerror = () => {
    const errorCallbacks = loadingScripts[url + ":error"] || [];
    delete loadingScripts[url];
    delete loadingScripts[url + ":error"];
    if (onError) {
      onError();
    }
    errorCallbacks.forEach((cb) => cb());
  };

  document.head.appendChild(script);

  return () => {
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
    delete loadedScripts[url];
  };
}

export function loadCheckoutPagoScript(options = {}) {
  return loadScript(checkoutPagoScriptVersion, options);
}

export function loadCheckoutSuscripcionesScript(options = {}) {
  return loadScript(checkoutScriptVersion, options);
}

/**
 * Drop-in replacement for react-load-script <Script /> component.
 */
export function ExternalScript({ url, onLoad, onError, attributes }) {
  useEffect(() => {
    return loadScript(url, { onLoad, onError, attributes });
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
